import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Tooltip,
  TextField,
  CircularProgress
} from "@mui/material";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  getPermissionDeniedMessage,
  isPermissionDenied,
} from "@1823-partners/core";
import {
  CellStyleModule,
  ClientSideRowModelModule,
  DateFilterModule,
  NumberFilterModule,
  QuickFilterModule,
  TextFilterModule,
} from "ag-grid-community";
import {
  ExcelExportModule,
} from "ag-grid-enterprise";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Download as DownloadIcon
} from "@mui/icons-material";

import api, { runHistory } from "../api";

// Stable row id: derive directly from the row data (PamGrid wraps this as
// (params) => getRowId(params.data) internally).
const getRunHistoryRowId = (data) => data.LOAD_ID;

// Format a duration in seconds as "Hh Mm Ss" (drops leading zero components).
// Returns '' for null/undefined/empty/non-numeric so single-row runs (where
// min and max timestamps coincide and the backend may return 0) don't read as
// "0s" — but a *real* 0-second run still does.
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '') return '';
  const total = Number(seconds);
  if (!Number.isFinite(total)) return '';
  const sign = total < 0 ? '-' : '';
  const abs = Math.abs(Math.round(total));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (h || m) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return sign + parts.join(' ');
};

// `isRunning`: some run is queued or running, so refresh silently every 30 s.
// `refreshToken`: reload when it changes (a run finished).
// `onRowsLoaded(rows)`: every successful load, so unfinished runs can be
// monitored again after a page reload.
const RunHistory = ({ isRunning = false, refreshToken = 0, onRowsLoaded }) => {
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const gridRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const requestSeqRef = useRef(0);
  const lastRefreshTokenRef = useRef(refreshToken);
  const onRowsLoadedRef = useRef(onRowsLoaded);
  onRowsLoadedRef.current = onRowsLoaded;

  // Per-user view-options (column order/width/visibility/sort/filter) backed
  // by the shared /grid-configs blueprint. Owning the hook here lets us bridge
  // its onGridReady into PamGrid so a default layout auto-applies once ready.
  const gridConfigs = useGridConfigs(api, {
    app: 'surveillance',
    gridId: 'run-history',
    gridApiRef: gridRef,
  });

  const loadRunHistory = useCallback(async (silent = false) => {
    // Only the latest request may update the grid (a slow earlier response
    // must not overwrite a newer one).
    const seq = ++requestSeqRef.current;
    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      const response = await runHistory();
      if (seq !== requestSeqRef.current) return;

      const formattedData = response.data.map(item => {
        const formattedItem = {};
        Object.keys(item).forEach(key => {
          // Convert field names to uppercase to match column definitions
          const upperKey = key.toUpperCase();
          formattedItem[upperKey] = item[key] === null ? '' : item[key];
        });
        return formattedItem;
      });

      setRowData(formattedData);
      onRowsLoadedRef.current?.(formattedData);
    } catch (error) {
      console.error("Error fetching run history:", error);
      if (seq === requestSeqRef.current && !silent) {
        if (isPermissionDenied(error)) {
          setError(getPermissionDeniedMessage(error));
        } else {
          setError("Failed to load run history. Please try again.");
        }
        setRowData([]);
      }
    } finally {
      // The latest load (silent or not) ends any spinner, including one
      // started by a load it superseded.
      if (seq === requestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Auto-refresh effect when surveillance is running
  useEffect(() => {
    if (isRunning) {
      // Set up interval to refresh every 30 seconds
      refreshIntervalRef.current = setInterval(() => {
        loadRunHistory(true); // Silent refresh
      }, 30000);
    } else {
      // Clear interval when surveillance stops
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isRunning, loadRunHistory]);

  // Load run history on component mount
  useEffect(() => {
    loadRunHistory();
  }, [loadRunHistory]);

  // A finished run: reload quietly (the mount load covers the initial token).
  useEffect(() => {
    if (refreshToken === lastRefreshTokenRef.current) return;
    lastRefreshTokenRef.current = refreshToken;
    loadRunHistory(true);
  }, [refreshToken, loadRunHistory]);

  const handleRefresh = useCallback(() => {
    loadRunHistory();
  }, [loadRunHistory]);

  const handleExportToExcel = useCallback(() => {
    if (gridRef.current) {
      const params = {
        fileName: `surveillance_run_history_${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Run History',
        exportMode: 'xlsx',
        skipColumnHeaders: false,
        skipColumnGroupHeaders: false,
        skipPinnedTop: false,
        skipPinnedBottom: false,
        allColumns: false,
        onlySelected: false,
        columnKeys: ['LOAD_ID', 'RUN_TYPE', 'RUN_DATE', 'ACCOUNTS', 'STATUS', 'NOTES', 'USER', 'DURATION_SECONDS', 'MODIFIED_DATE']
      };

      gridRef.current.api.exportDataAsExcel(params);
    }
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "Load ID",
      headerTooltip: "Unique identifier for the surveillance run",
      field: "LOAD_ID",
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: "agTextColumnFilter",
      flex: 1,
      pinned: 'left'
    },
    {
      headerName: "Run Type",
      headerTooltip: "Type of surveillance run",
      field: "RUN_TYPE",
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: "agTextColumnFilter",
      flex: 1
    },
    {
      headerName: "Run Date",
      headerTooltip: "Date when the surveillance was executed",
      field: "RUN_DATE",
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: "agDateColumnFilter",
      flex: 1,
      valueFormatter: (params) => params.value ?? ''
    },
    {
      headerName: "Accounts",
      headerTooltip: "Accounts a Run Book was requested for (blank for a monthly run)",
      field: "ACCOUNTS",
      cellStyle: { textAlign: "left" },
      sortable: true,
      filter: "agTextColumnFilter",
      wrapText: true,
      autoHeight: true,
      flex: 1
    },
    {
      headerName: "Status",
      headerTooltip: "Current status of the surveillance run",
      field: "STATUS",
      cellStyle: (params) => {
        const status = String(params.value ?? '').toLowerCase().replace(/_/g, ' ');
        if (status === 'completed' || status === 'success') {
          return { textAlign: "center", backgroundColor: '#e8f5e8', color: '#2e7d2e' };
        } else if (status === 'failed' || status === 'error') {
          return { textAlign: "center", backgroundColor: '#ffeaea', color: '#d32f2f' };
        } else if (status === 'running' || status === 'in progress') {
          return { textAlign: "center", backgroundColor: '#fff3cd', color: '#856404' };
        } else if (status === 'requested') {
          // Queued on Deacon, waiting for the Beacon run job to pick it up.
          return { textAlign: "center", backgroundColor: '#e3f2fd', color: '#0d47a1' };
        }
        return { textAlign: "center" };
      },
      sortable: true,
      filter: "agTextColumnFilter",
      flex: 1
    },
    {
      headerName: "Notes",
      headerTooltip: "Additional notes or comments about the run",
      field: "NOTES",
      cellStyle: { textAlign: "left" },
      sortable: true,
      filter: "agTextColumnFilter",
      wrapText: true,
      autoHeight: true,
      flex: 1
    },
    {
      headerName: "User",
      headerTooltip: "User who executed the surveillance",
      field: "USER",
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: "agTextColumnFilter",
      flex: 1
    },
    {
      headerName: "Total Time",
      headerTooltip: "Wall-clock duration of the run (last status timestamp minus first status timestamp for the load_id)",
      field: "DURATION_SECONDS",
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: "agNumberColumnFilter",
      flex: 1,
      valueFormatter: (params) => formatDuration(params.value),
    },
    {
      headerName: "Modified Date",
      headerTooltip: "Date when the record was last modified",
      field: "MODIFIED_DATE",
      cellStyle: { textAlign: "center" },
      sortable: true,
      filter: "agDateColumnFilter",
      flex: 1,
      editable: false,
      valueFormatter: (params) => {
        if (params.value) {
          const date = new Date(params.value);
          return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        }
        return '';
      }
    }
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    headerClass: 'ag-header-cell-custom'
  }), []);

  const gridModules = useMemo(() => [
    CellStyleModule,
    ClientSideRowModelModule,
    DateFilterModule,
    NumberFilterModule,
    QuickFilterModule,
    TextFilterModule,
    ExcelExportModule,
  ], []);

  const onQuickFilterChanged = useCallback((event) => {
    setQuickFilterText(event.target.value);
  }, []);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
        Surveillance Run History
      </Typography>

      {/* Controls Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 2,
        p: 2,
        backgroundColor: theme.palette.grey[50],
        borderRadius: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="action" />
          <TextField
            placeholder="Search across all columns..."
            value={quickFilterText}
            onChange={onQuickFilterChanged}
            variant="outlined"
            size="small"
            sx={{ minWidth: { xs: '100%', sm: '300px' } }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <GridConfigMenu controller={gridConfigs} />
          <Tooltip title="Refresh run history data">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightblue",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(173, 216, 230, 0.8)'
                  }
                }}
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export run history to Excel file">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightgreen",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(144, 238, 144, 0.8)'
                  }
                }}
                onClick={handleExportToExcel}
                startIcon={<DownloadIcon />}
                disabled={rowData.length === 0}
              >
                Export Excel
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography color="error">
            {error}
          </Typography>
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading run history...</Typography>
        </Box>
      )}

      {!isLoading && rowData.length === 0 && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No run history data available.
          </Typography>
        </Box>
      )}

      {/* Grid Section */}
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getRunHistoryRowId}
        modules={gridModules}
        height="500px"
        headerHeight={40}
        // This component renders its own header, search box, export button,
        // footer note, and loading spinner — keep PamGrid's built-in chrome
        // (toolbar/quick-filter/export/footer/loading overlay) off so behavior
        // is unchanged.
        showToolbar={false}
        showQuickFilter={false}
        showExport={false}
        showFooter={false}
        quickFilterText={quickFilterText}
        // Auto-apply default layout once the grid API is live.
        onGridReady={gridConfigs.onGridReady}
        // Mirror filter changes into the local stash; column gestures ride
        // through agGridProps as stashBindings.
        onFilterChanged={gridConfigs.onGridStateChanged}
        // Everything the raw grid passed that isn't a PamGrid named prop is
        // preserved verbatim via agGridProps (overrides PamGrid defaults).
        agGridProps={{
          domLayout: 'normal',
          enableBrowserTooltips: true,
          rowHeight: 32,
          suppressHorizontalScroll: false,
          suppressRowClickSelection: true,
          readOnlyEdit: true,
          ...gridConfigs.stashBindings,
        }}
      />

      <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> This grid displays the history of surveillance runs including their status, execution dates, and any associated notes.
          Use the search box to filter across all columns or click column headers to sort. The grid is read-only and cannot be edited.
          A REQUESTED run is queued for the Beacon run job; while any run is queued or running, this list refreshes automatically.
          {rowData.length > 0 && (
            <span style={{ display: 'block', marginTop: '4px' }}>
              <strong>Total runs displayed:</strong> {rowData.length}
            </span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default RunHistory;